import { useState, useRef, useEffect } from "react";
import { X, Plus, Image as ImageIcon, Type, Trash2, Upload, Loader2, ChevronRight, FolderTree, Eye, EyeOff, Calendar, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useCategories, Category } from "@/hooks/useCategories";
import { Article } from "@/hooks/useArticles";
import { format } from "date-fns";
import { it } from "date-fns/locale";

interface ContentBlock {
  id: string;
  type: "text" | "image";
  content: string;
}

interface ArticleEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onArticleCreated: () => void;
  editArticle?: Article | null;
}

const ArticleEditor = ({ isOpen, onClose, onArticleCreated, editArticle }: ArticleEditorProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  
  const coverInputRef = useRef<HTMLInputElement>(null);
  const contentImageInputRef = useRef<HTMLInputElement>(null);
  const currentBlockIdRef = useRef<string | null>(null);
  
  const { categories, categoryTree, loading: categoriesLoading } = useCategories();

  const isEditMode = !!editArticle;

  // Load article data when editing
  useEffect(() => {
    if (editArticle && isOpen) {
      setTitle(editArticle.title);
      setDescription(editArticle.description);
      setCoverImage(editArticle.image);
      setCoverImageFile(null);
      
      // Load content blocks
      if (editArticle.content && Array.isArray(editArticle.content)) {
        setContentBlocks(
          editArticle.content.map((block: { type: string; content: string }) => ({
            id: crypto.randomUUID(),
            type: block.type as "text" | "image",
            content: block.content,
          }))
        );
      } else {
        setContentBlocks([]);
      }
      
      // Load categories
      loadArticleCategories(editArticle.id);
    } else if (!isOpen) {
      resetForm();
    }
  }, [editArticle, isOpen]);

  const loadArticleCategories = async (articleId: string) => {
    try {
      const { data, error } = await supabase
        .from("article_categories")
        .select("category_id")
        .eq("article_id", articleId);

      if (error) throw error;

      if (data) {
        setSelectedCategories(new Set(data.map((ac) => ac.category_id)));
      }
    } catch (error) {
      console.error("Error loading article categories:", error);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleCoverImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine valida");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'immagine deve essere inferiore a 10MB");
      return;
    }

    setCoverImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const { error: uploadError } = await supabase.storage
      .from("article-images")
      .upload(path, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from("article-images")
      .getPublicUrl(path);

    return publicUrl;
  };

  const addTextBlock = () => {
    setContentBlocks([
      ...contentBlocks,
      { id: crypto.randomUUID(), type: "text", content: "" },
    ]);
  };

  const addImageBlock = () => {
    const newId = crypto.randomUUID();
    currentBlockIdRef.current = newId;
    contentImageInputRef.current?.click();
  };

  const handleContentImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentBlockIdRef.current) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine valida");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'immagine deve essere inferiore a 10MB");
      return;
    }

    const blockId = currentBlockIdRef.current;
    setUploadingImage(blockId);

    try {
      const filePath = `content/${Date.now()}-${file.name}`;
      const publicUrl = await uploadImage(file, filePath);
      
      setContentBlocks([
        ...contentBlocks,
        { id: blockId, type: "image", content: publicUrl },
      ]);
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Errore durante il caricamento dell'immagine");
    } finally {
      setUploadingImage(null);
      currentBlockIdRef.current = null;
    }
  };

  const updateBlockContent = (id: string, content: string) => {
    setContentBlocks(
      contentBlocks.map((block) =>
        block.id === id ? { ...block, content } : block
      )
    );
  };

  const removeBlock = (id: string) => {
    setContentBlocks(contentBlocks.filter((block) => block.id !== id));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }

    if (!description.trim()) {
      toast.error("Inserisci una descrizione");
      return;
    }

    if (!coverImage && !coverImageFile) {
      toast.error("Seleziona un'immagine di copertina");
      return;
    }

    setIsSaving(true);

    try {
      let coverUrl = coverImage;

      // Upload new cover image if changed
      if (coverImageFile) {
        const coverPath = `covers/${Date.now()}-${coverImageFile.name}`;
        coverUrl = await uploadImage(coverImageFile, coverPath);
      }

      if (isEditMode && editArticle) {
        // Update existing article
        const publishedAt = isScheduled && scheduledDate && scheduledTime 
          ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
          : new Date().toISOString();
        
        const { error } = await supabase
          .from("articles")
          .update({
            title: title.trim(),
            description: description.trim(),
            image_key: coverUrl,
            content: contentBlocks.map(({ type, content }) => ({ type, content })),
            published_at: publishedAt,
            is_published: !isScheduled || new Date(publishedAt) <= new Date(),
          })
          .eq("id", editArticle.id);

        if (error) throw error;

        // Update categories - delete existing and insert new
        await supabase
          .from("article_categories")
          .delete()
          .eq("article_id", editArticle.id);

        if (selectedCategories.size > 0) {
          const categoryInserts = Array.from(selectedCategories).map((categoryId) => ({
            article_id: editArticle.id,
            category_id: categoryId,
          }));

          await supabase.from("article_categories").insert(categoryInserts);
        }

        toast.success(isScheduled ? "Articolo programmato!" : "Articolo aggiornato!");
      } else {
        // Create new article
        const slug = generateSlug(title) + "-" + Date.now();
        const publishedAt = isScheduled && scheduledDate && scheduledTime 
          ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
          : new Date().toISOString();
        
        const { data: articleData, error } = await supabase.from("articles").insert({
          title: title.trim(),
          description: description.trim(),
          slug,
          image_key: coverUrl,
          content: contentBlocks.map(({ type, content }) => ({ type, content })),
          published_at: publishedAt,
          is_published: !isScheduled || new Date(publishedAt) <= new Date(),
        }).select().single();

        if (error) throw error;

        // Insert article categories
        if (selectedCategories.size > 0 && articleData) {
          const categoryInserts = Array.from(selectedCategories).map((categoryId) => ({
            article_id: articleData.id,
            category_id: categoryId,
          }));

          const { error: catError } = await supabase
            .from("article_categories")
            .insert(categoryInserts);

          if (catError) {
            console.error("Error adding categories:", catError);
          }
        }

        toast.success(isScheduled ? "Articolo programmato!" : "Articolo pubblicato!");

        // Send push notification for new published article
        if (!isScheduled) {
          try {
            const { sendPushToAll } = await import("@/lib/pushNotifications");
            await sendPushToAll(
              `📰 ${title.trim()}`,
              description.trim().substring(0, 100),
              "/news",
              "new-article"
            );
          } catch (e) { console.log("Push notification failed:", e); }
        }
      }

      resetForm();
      onArticleCreated();
      onClose();
    } catch (error) {
      console.error("Error saving article:", error);
      toast.error("Errore durante il salvataggio dell'articolo");
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCoverImage(null);
    setCoverImageFile(null);
    setContentBlocks([]);
    setSelectedCategories(new Set());
    setIsScheduled(false);
    setScheduledDate("");
    setScheduledTime("");
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const renderCategoryCheckbox = (category: Category, level: number = 0) => (
    <div key={category.id}>
      <div
        className="flex items-center gap-2 py-2 hover:bg-muted/50 rounded-lg px-2 transition-colors"
        style={{ marginLeft: level * 20 }}
      >
        {level > 0 && (
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
        )}
        <Checkbox
          id={category.id}
          checked={selectedCategories.has(category.id)}
          onCheckedChange={() => toggleCategory(category.id)}
        />
        <label
          htmlFor={category.id}
          className="text-sm cursor-pointer flex-1"
        >
          {category.name}
        </label>
      </div>
      {category.children?.map((child) => renderCategoryCheckbox(child, level + 1))}
    </div>
  );

  // Preview Component
  const ArticlePreview = () => (
    <div className="h-full overflow-y-auto bg-card rounded-xl border border-border">
      {/* Preview Header */}
      <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border px-4 py-3 z-10">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Eye className="w-4 h-4" />
          Anteprima Live
        </div>
      </div>

      <div className="p-4">
        {/* Cover Image Preview */}
        {coverImage ? (
          <div className="relative rounded-xl overflow-hidden mb-4">
            <img
              src={coverImage}
              alt="Cover preview"
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        ) : (
          <div className="w-full h-48 bg-muted rounded-xl mb-4 flex items-center justify-center">
            <span className="text-muted-foreground text-sm">Immagine di copertina</span>
          </div>
        )}

        {/* Title Preview */}
        <h1 className="text-2xl font-bold text-foreground mb-2">
          {title || <span className="text-muted-foreground italic">Titolo dell'articolo</span>}
        </h1>

        {/* Date and Categories */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-sm text-muted-foreground">
            {format(new Date(), "d MMMM yyyy", { locale: it })}
          </span>
          {selectedCategories.size > 0 && (
            <>
              <span className="text-muted-foreground">•</span>
              <div className="flex flex-wrap gap-1">
                {Array.from(selectedCategories).map((catId) => {
                  const cat = categories.find((c) => c.id === catId);
                  return cat ? (
                    <span
                      key={catId}
                      className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full"
                    >
                      {cat.name}
                    </span>
                  ) : null;
                })}
              </div>
            </>
          )}
        </div>

        {/* Description Preview */}
        <p className="text-muted-foreground leading-relaxed mb-6">
          {description || <span className="italic">Descrizione dell'articolo...</span>}
        </p>

        {/* Content Blocks Preview */}
        {contentBlocks.length > 0 ? (
          <div className="space-y-4">
            {contentBlocks.map((block, index) => (
              <div key={block.id}>
                {block.type === "text" ? (
                  <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">
                    {block.content || <span className="text-muted-foreground italic">Blocco di testo #{index + 1}</span>}
                  </p>
                ) : (
                  <div className="rounded-xl overflow-hidden">
                    <img
                      src={block.content}
                      alt={`Content ${index + 1}`}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground border border-dashed rounded-xl">
            <p>Il contenuto apparirà qui...</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
      <DrawerContent className="h-[95vh] bg-background border-t border-border">
        <DrawerHeader className="relative border-b border-border">
          <button
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-all"
          >
            <X className="w-5 h-5" />
          </button>
          <DrawerTitle className="text-xl font-bold text-center">
            {isEditMode ? "Modifica Articolo" : "Nuovo Articolo"}
          </DrawerTitle>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              className="gap-1.5"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span className="hidden sm:inline">{showPreview ? "Nascondi" : "Anteprima"}</span>
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size="sm"
              className={isScheduled ? "bg-amber-600 hover:bg-amber-700" : ""}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isScheduled ? (
                <>
                  <Calendar className="w-4 h-4 mr-1" />
                  Programma
                </>
              ) : isEditMode ? (
                "Salva"
              ) : (
                "Pubblica ora"
              )}
            </Button>
          </div>
        </DrawerHeader>

        <div className={`flex-1 overflow-hidden p-4 ${showPreview ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''}`}>
          {/* Editor Panel */}
          <div className="overflow-y-auto space-y-6 pr-2">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Titolo</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titolo dell'articolo"
              className="text-lg"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Descrizione breve
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrizione per l'anteprima..."
              rows={2}
            />
          </div>

          {/* Cover Image */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Immagine di copertina
            </label>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverImageChange}
              className="hidden"
            />
            {coverImage ? (
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src={coverImage}
                  alt="Cover"
                  className="w-full h-48 object-cover"
                />
                <button
                  onClick={() => {
                    setCoverImage(null);
                    setCoverImageFile(null);
                  }}
                  className="absolute top-2 right-2 p-2 bg-destructive/80 rounded-full text-destructive-foreground hover:bg-destructive transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => coverInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:bg-muted/50 transition-colors"
              >
                <Upload className="w-8 h-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Clicca per caricare
                </span>
              </button>
            )}
          </div>

          {/* Categories Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <FolderTree className="w-4 h-4" />
              Categorie
            </label>
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            ) : categoryTree.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground border border-dashed rounded-xl text-sm">
                Nessuna categoria disponibile
              </div>
            ) : (
              <div className="border border-border rounded-xl p-3 max-h-48 overflow-y-auto">
                {categoryTree.map((category) => renderCategoryCheckbox(category))}
              </div>
            )}
            {selectedCategories.size > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {Array.from(selectedCategories).map((catId) => {
                  const cat = categories.find((c) => c.id === catId);
                  return cat ? (
                    <span
                      key={catId}
                      className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                    >
                      {cat.name}
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Scheduling Section */}
          <div className="space-y-3 p-4 bg-muted/30 rounded-2xl">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Programma uscita
              </label>
              <Switch
                checked={isScheduled}
                onCheckedChange={setIsScheduled}
              />
            </div>
            
            {isScheduled && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Data</label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={format(new Date(), "yyyy-MM-dd")}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Ora</label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>
            )}
            
            {!isScheduled && (
              <p className="text-xs text-muted-foreground">
                L'articolo verrà pubblicato immediatamente
              </p>
            )}
          </div>

          {/* Content Blocks */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Contenuto
              </label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addTextBlock}
                  className="gap-1"
                >
                  <Type className="w-4 h-4" />
                  Testo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addImageBlock}
                  disabled={!!uploadingImage}
                  className="gap-1"
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4" />
                  )}
                  Immagine
                </Button>
              </div>
            </div>

            <input
              ref={contentImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleContentImageChange}
              className="hidden"
            />

            <AnimatePresence mode="popLayout">
              {contentBlocks.map((block, index) => (
                <motion.div
                  key={block.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="relative group"
                >
                  <div className="absolute -left-8 top-2 text-xs text-muted-foreground">
                    {index + 1}
                  </div>
                  {block.type === "text" ? (
                    <Textarea
                      value={block.content}
                      onChange={(e) => updateBlockContent(block.id, e.target.value)}
                      placeholder="Scrivi il testo qui..."
                      rows={4}
                      className="pr-10"
                    />
                  ) : (
                    <div className="relative rounded-xl overflow-hidden">
                      <img
                        src={block.content}
                        alt={`Content ${index + 1}`}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}
                  <button
                    onClick={() => removeBlock(block.id)}
                    className="absolute top-2 right-2 p-1.5 bg-destructive/80 rounded-full text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>

            {contentBlocks.length === 0 && (
              <div className="text-center py-8 text-muted-foreground border border-dashed rounded-xl">
                <p>Aggiungi blocchi di testo o immagini</p>
              </div>
            )}
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="hidden lg:block h-full">
              <ArticlePreview />
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ArticleEditor;
